import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/lib/api";

/**
 * Overview tab for /admin/creators/:id. Renders:
 *  - Next actions (drafted placements, live-not-indexed, missing playbook,
 *    next tracked-run date). Each item deep-links to the resolving tab.
 *  - Contact card (contact email once captured, Clerk sign-in email,
 *    platform sign-up email {handle}@samoony.com).
 *  - Playbook checklist with per-step status and expand-to-copy field kit.
 */

function CopyChip({ value, label, testid }) {
    const [ok, setOk] = useState(false);
    const onCopy = async (e) => {
        e.stopPropagation();
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setOk(true);
            toast.success(`${label} copied.`);
            setTimeout(() => setOk(false), 1500);
        } catch {
            toast.error("Could not copy.");
        }
    };
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onCopy}
            className="text-[11px] font-mono uppercase tracking-wider text-black/50 hover:text-ink px-2 py-0.5 rounded-full border border-black/10"
        >
            {ok ? "Copied" : "Copy"}
        </button>
    );
}

function StatusDot({ status }) {
    const map = {
        done: { cls: "bg-signal", label: "done" },
        in_progress: { cls: "bg-electric", label: "in progress" },
        todo: { cls: "bg-black/20", label: "to do" },
        optional: { cls: "bg-black/10 ring-1 ring-black/20", label: "opt-in" },
        not_eligible: { cls: "bg-black/5 ring-1 ring-black/10", label: "n/a" },
        skipped: { cls: "bg-black/5 ring-1 ring-black/10", label: "skipped" },
    };
    const m = map[status] || map.todo;
    return (
        <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-black/60">
            <span className={`w-2.5 h-2.5 rounded-full ${m.cls}`} aria-hidden="true" />
            {m.label}
        </span>
    );
}

function PlaybookOverrideControls({ creatorId, step, onChanged }) {
    const { request } = useApi();
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");
    const [reason, setReason] = useState("");

    const apply = async () => {
        if (!status) {
            toast.error("Pick a status.");
            return;
        }
        if (!reason.trim()) {
            toast.error("Add a short reason.");
            return;
        }
        setBusy(true);
        try {
            await request(`/admin/creators/${creatorId}/playbook-override`, {
                method: "PATCH",
                body: { step_key: step.key, status, reason: reason.trim() },
            });
            toast.success("Override saved.");
            setStatus("");
            setReason("");
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Could not save.");
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        setBusy(true);
        try {
            await request(`/admin/creators/${creatorId}/playbook-override`, {
                method: "PATCH",
                body: { step_key: step.key, status: "clear" },
            });
            toast.success("Override cleared.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Could not clear.");
        } finally {
            setBusy(false);
        }
    };

    const isOverridden =
        step.status === "not_eligible" || step.status === "skipped";

    return (
        <div
            data-testid={`playbook-override-${step.key}`}
            className="mt-3 pt-3 border-t border-black/5 flex flex-col gap-2"
        >
            <div className="text-[11px] font-mono uppercase tracking-wider text-black/50">
                Override
            </div>
            {isOverridden ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs text-black/60">
                        Currently{" "}
                        <strong>{step.status.replace("_", " ")}</strong>
                        {step.note ? ` · ${step.note}` : ""}
                    </div>
                    <button
                        type="button"
                        data-testid={`playbook-override-clear-${step.key}`}
                        onClick={clear}
                        disabled={busy}
                        className="samoony-chip disabled:opacity-30"
                    >
                        {busy ? "..." : "Clear"}
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        data-testid={`playbook-override-status-${step.key}`}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="samoony-input !h-9"
                    >
                        <option value="">Mark as…</option>
                        <option value="not_eligible">Not eligible</option>
                        <option value="skipped">Skipped</option>
                    </select>
                    <input
                        data-testid={`playbook-override-reason-${step.key}`}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Short reason"
                        className="samoony-input !h-9 flex-1 min-w-0"
                    />
                    <button
                        type="button"
                        data-testid={`playbook-override-save-${step.key}`}
                        onClick={apply}
                        disabled={busy}
                        className="samoony-chip disabled:opacity-30"
                    >
                        {busy ? "..." : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default function CreatorOverview({ creator, onJumpTab }) {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState({}); // step_key -> expanded

    const load = useCallback(async () => {
        if (!creator?.id) return;
        setLoading(true);
        try {
            const res = await request(`/admin/creators/${creator.id}/overview`);
            setData(res || null);
        } catch (err) {
            toast.error(err?.message || "Could not load overview.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [creator?.id, request]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="text-sm text-black/50">Loading overview...</div>
        );
    }
    if (!data) return null;

    const drafted = data.placements?.drafted || [];
    const lni = data.placements?.live_not_indexed || [];
    const missingPlaybook = (data.playbook || []).filter(
        (s) => s.status === "todo",
    );
    const nextRunIso = data.next_tracked_run?.iso;
    const nextRunLabel = nextRunIso
        ? new Date(nextRunIso).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "short",
            day: "numeric",
        })
        : "";

    const nothingToDo =
        !drafted.length &&
        !lni.length &&
        !missingPlaybook.length;

    const fk = data.field_kit || {};
    const contact = data.contact || {};

    return (
        <div className="flex flex-col gap-5">
            {/* Next actions */}
            <section
                data-testid="creator-overview-next-actions"
                className="bg-white border border-black/10 rounded-2xl p-5"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="samoony-label">Next actions</div>
                    {nextRunLabel && (
                        <div className="text-[11px] font-mono uppercase tracking-wider text-black/50">
                            Next tracked run: {nextRunLabel}
                        </div>
                    )}
                </div>
                {data.latest_sighting ? (
                    <div
                        data-testid="creator-overview-latest-sighting"
                        className="mb-3 text-xs text-black/60"
                    >
                        Latest sighting:{" "}
                        <strong>
                            {data.latest_sighting.result === "named"
                                ? `Named${data.latest_sighting.rank ? ` · ${data.latest_sighting.rank}` : ""}`
                                : "Not named"}
                        </strong>{" "}
                        · {data.latest_sighting.engine} ·{" "}
                        {data.latest_sighting.date}
                    </div>
                ) : null}
                {nothingToDo ? (
                    <p
                        data-testid="creator-overview-next-actions-empty"
                        className="text-sm text-black/60"
                    >
                        Nothing to action right now.
                    </p>
                ) : (
                    <ul className="flex flex-col gap-2">
                        {drafted.length > 0 && (
                            <li>
                                <button
                                    type="button"
                                    data-testid="creator-overview-action-drafted"
                                    onClick={() => onJumpTab?.("placements")}
                                    className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-xl bg-black/[0.03] hover:bg-black/5"
                                >
                                    <span className="text-sm">
                                        <strong className="font-medium">
                                            {drafted.length}
                                        </strong>{" "}
                                        drafted{" "}
                                        {drafted.length === 1
                                            ? "placement"
                                            : "placements"}{" "}
                                        waiting to go live
                                    </span>
                                    <span className="text-xs text-black/40">
                                        Open placements →
                                    </span>
                                </button>
                            </li>
                        )}
                        {lni.length > 0 && (
                            <li>
                                <button
                                    type="button"
                                    data-testid="creator-overview-action-lni"
                                    onClick={() => onJumpTab?.("placements")}
                                    className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-xl bg-black/[0.03] hover:bg-black/5"
                                >
                                    <span className="text-sm">
                                        <strong className="font-medium">
                                            {lni.length}
                                        </strong>{" "}
                                        live but not yet indexed
                                    </span>
                                    <span className="text-xs text-black/40">
                                        Open placements →
                                    </span>
                                </button>
                            </li>
                        )}
                        {missingPlaybook.length > 0 && (
                            <li>
                                <button
                                    type="button"
                                    data-testid="creator-overview-action-playbook"
                                    onClick={() => {
                                        const el = document.getElementById(
                                            "creator-overview-playbook",
                                        );
                                        if (el) {
                                            el.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            });
                                        }
                                    }}
                                    className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-xl bg-black/[0.03] hover:bg-black/5"
                                >
                                    <span className="text-sm">
                                        <strong className="font-medium">
                                            {missingPlaybook.length}
                                        </strong>{" "}
                                        standard playbook{" "}
                                        {missingPlaybook.length === 1
                                            ? "step"
                                            : "steps"}{" "}
                                        remaining
                                    </span>
                                    <span className="text-xs text-black/40">
                                        See checklist →
                                    </span>
                                </button>
                            </li>
                        )}
                    </ul>
                )}
            </section>

            {/* Contact card */}
            <section
                data-testid="creator-overview-contact"
                className="bg-white border border-black/10 rounded-2xl p-5"
            >
                <div className="samoony-label mb-3">Contact</div>
                <ContactRow
                    label="Contact email"
                    value={contact.contact_email}
                    emptyLabel="Not captured yet"
                    testid="contact-row-contact-email"
                    primary
                />
                <ContactRow
                    label="Sign-in email"
                    value={contact.sign_in_email}
                    testid="contact-row-sign-in-email"
                />
                <ContactRow
                    label="Platform sign-up email"
                    value={contact.platform_signup_email}
                    testid="contact-row-platform-email"
                    note="Goes to the admin inbox"
                />
            </section>

            {/* Playbook */}
            <section
                id="creator-overview-playbook"
                data-testid="creator-overview-playbook"
                className="bg-white border border-black/10 rounded-2xl p-5"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="samoony-label">Playbook</div>
                    <div className="text-xs text-black/40">
                        Field kit at the bottom of each step
                    </div>
                </div>
                <ul className="flex flex-col divide-y divide-black/5">
                    {(data.playbook || []).map((step) => (
                        <li
                            key={step.key}
                            data-testid={`playbook-row-${step.key}`}
                            className="py-2"
                        >
                            <button
                                type="button"
                                data-testid={`playbook-toggle-${step.key}`}
                                onClick={() =>
                                    setOpen((prev) => ({
                                        ...prev,
                                        [step.key]: !prev[step.key],
                                    }))
                                }
                                className="w-full flex items-center justify-between gap-3 text-left"
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm">
                                            {step.label}
                                        </span>
                                        {step.optional && (
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-black/40">
                                                Opt-in
                                            </span>
                                        )}
                                    </div>
                                    {step.note ? (
                                        <div className="text-xs text-black/50 mt-0.5">
                                            {step.note}
                                        </div>
                                    ) : null}
                                </div>
                                <StatusDot status={step.status} />
                            </button>
                            {open[step.key] && (
                                <div
                                    data-testid={`playbook-fieldkit-${step.key}`}
                                    className="mt-3 bg-black/[0.03] rounded-xl p-3"
                                >
                                    <div className="text-[11px] font-mono uppercase tracking-wider text-black/50 mb-2">
                                        Field kit
                                    </div>
                                    <FieldKit fk={fk} stepKey={step.key} />
                                    <PlaybookOverrideControls
                                        creatorId={creator?.id}
                                        step={step}
                                        onChanged={load}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}

function ContactRow({ label, value, emptyLabel, note, testid, primary }) {
    const text = value || emptyLabel || "—";
    const canCopy = !!value;
    return (
        <div
            data-testid={testid}
            className="flex items-start justify-between gap-3 py-2 border-b border-black/5 last:border-b-0"
        >
            <div className="min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-wider text-black/50">
                    {label}
                </div>
                <div
                    className={`${primary ? "text-ink" : "text-black/80"} text-sm break-all`}
                >
                    {text}
                </div>
                {note ? (
                    <div className="text-xs text-black/40 mt-0.5">{note}</div>
                ) : null}
            </div>
            {canCopy && (
                <CopyChip
                    value={value}
                    label={label}
                    testid={`${testid}-copy`}
                />
            )}
        </div>
    );
}

function FieldKit({ fk }) {
    const rows = [
        ["Name", fk.name],
        ["Bio (max 150)", fk.bio],
        ["City", fk.city],
        ["Primary niche", fk.primary_niche],
        ["Samoony page", fk.samoony_url],
        ["Instagram", fk.instagram],
        ["TikTok", fk.tiktok],
        ["YouTube", fk.youtube],
        ["LinkedIn", fk.linkedin],
    ];
    return (
        <dl className="flex flex-col gap-2">
            {rows.map(([k, v]) => (
                <div
                    key={k}
                    className="flex items-start justify-between gap-3"
                >
                    <div className="min-w-0 flex-1">
                        <dt className="text-[11px] font-mono uppercase tracking-wider text-black/40">
                            {k}
                        </dt>
                        <dd className="text-sm text-ink break-all">
                            {v || "—"}
                        </dd>
                    </div>
                    {v && (
                        <CopyChip
                            value={v}
                            label={k}
                            testid={`fieldkit-copy-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                    )}
                </div>
            ))}
        </dl>
    );
}
